import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DateValue, MonthPickerInput } from '@mantine/dates';
import { AreaChart } from '@mantine/charts';
import { useMemo, useState } from 'react';
import { useGetSalesdReport } from '../../../hooks/reports/queries';
import momentTz from 'moment-timezone';
import { exportSalesReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { getRangeDate } from '../datePickerHelper';

const MonthlySalesReport = () => {
  const salesReportType = 'month';
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('year').toDate(),
    moment().endOf('year').toDate()
  ]);
  const { fromDate, toDate } = getRangeDate(dateRangePicker, 'month');

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetSalesdReport(searchParamStoreId, {
    fromDate,
    toDate,
    timezone: momentTz.tz.guess(true),
    type: salesReportType
  });

  const salesData: {
    date: string;
    ordersQty: number;
    ordersUnpaidTotal: number;
    salesTotal: number;
    totalProductPieces: number;
  }[] = useMemo(() => {
    return data?.tableInfo;
  }, [data]);
  const totalRowsCount = useMemo(() => data?.totalRows, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:monthSalesReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <MonthPickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportSalesReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: momentTz.tz.guess(true),
                      type: salesReportType
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>

        <Grid.Col>
          <AreaChart
            h={400}
            data={Object.keys(data?.graphData || {})?.map((dayKey: string) => {
              const sale = data?.graphData[dayKey];
              return {
                salesDate: dayKey,
                totalSale: sale
              };
            })}
            dataKey="salesDate"
            series={[{ name: 'totalSale', color: 'indigo.6', label: t('FieldName:orders') }]}
            curveType="linear"
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            salesData={salesData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
            footerData={data?.totalRowStats}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default MonthlySalesReport;
